'use client';

import { useRef } from 'react';
import { GNBList, Button, Box } from '@wishket/design-system';
import { useClickOutside } from '@wishket/yogokit';
import { useRouter } from 'next/navigation';
import { twJoin } from 'tailwind-merge';

import { Link } from '@/shared/Components';
import { PATH } from '@/shared/constants';
import { Profile } from '@/shared/types';

interface UserMenuPopoverProps {
  isOpen: boolean;
  onClose: () => void;
  profile: Profile | null;
  nextPath: string;
  openProfileModal: () => void;
  openProfileImageChangeModal: () => void;
}

const UserMenuPopover = ({
  isOpen,
  onClose,
  profile,
  nextPath,
  openProfileModal,
  openProfileImageChangeModal,
}: UserMenuPopoverProps) => {
  const router = useRouter();
  const ref = useRef<HTMLDivElement>(null);

  useClickOutside(ref, () => {
    if (isOpen) onClose();
  });

  const mouseOverLink = (path: string) => {
    router.prefetch(path);
  };

  const clickLink = (path: string) => {
    onClose();
    router.push(path);
  };

  const clickProfileImageChange = () => {
    onClose();
    openProfileImageChangeModal();
  };

  const clickProfileEdit = () => {
    onClose();
    openProfileModal();
  };

  if (!profile) return null;

  return (
    <Box
      data-testid="user-menu-popover"
      ref={ref}
      className="hidden desktop:block"
    >
      <GNBList.Root
        type="user"
        className={twJoin(
          'right-0 top-[44px] z-10 origin-top transition-transform',
          isOpen ? 'scale-100' : 'scale-0'
        )}
      >
        <GNBList.UserInfo
          userId={profile?.name}
          email={profile?.email}
          onClick={clickProfileImageChange}
          imgUrl={profile?.imgSrc}
        >
          <Button
            size="sm"
            onClick={clickProfileEdit}
            className="w-full text-w-gray-600 hover:text-y-primary"
          >
            프로필 수정
          </Button>
          {profile.isAuthor && (
            <Box
              onMouseOver={() =>
                mouseOverLink(`${PATH.HOME}/@${profile.authorIdentifier}`)
              }
              onClick={() =>
                clickLink(`${PATH.HOME}/@${profile.authorIdentifier}`)
              }
            >
              <Button
                size="sm"
                className="w-full border-primary bg-y-primary-10 text-y-primary"
              >
                작가 프로필
              </Button>
            </Box>
          )}
        </GNBList.UserInfo>
        <GNBList.List>
          <Box
            onMouseOver={() => mouseOverLink(PATH.MY_COLLECTION)}
            onClick={() => clickLink(PATH.MY_COLLECTION)}
          >
            <GNBList.Item hasNew={profile.hasNewCollection}>
              내 컬렉션
            </GNBList.Item>
          </Box>

          <Link target="_blank" href={PATH.AUTH_SETTINGS}>
            <GNBList.Item>계정 설정</GNBList.Item>
          </Link>

          <Box
            onMouseOver={() => mouseOverLink(PATH.NOTIFICATION_SETTINGS)}
            onClick={() => clickLink(PATH.NOTIFICATION_SETTINGS)}
          >
            <GNBList.Item>알림 설정</GNBList.Item>
          </Box>
        </GNBList.List>
        <GNBList.SubList>
          <Link href={`${PATH.AUTH_LOGOUT}?next=${nextPath}`} prefetch={false}>
            <GNBList.Item>로그아웃</GNBList.Item>
          </Link>
        </GNBList.SubList>
      </GNBList.Root>
    </Box>
  );
};

export default UserMenuPopover;
